import React from "react";

import { ReactComponent as CloseIcon } from "../../../../assets/svg/close.svg";
import { ReactComponent as ArrowLeft } from "../../../../assets/svg/keyboard_arrow_left.svg";
import { ReactComponent as WarningIcon } from "../../../../assets/svg/warning_green.svg";
import { ReactComponent as RightArrowIcon } from "../../../../assets/svg/arrow_right.svg";
import { ITeacherSurvey, ITeachingHintsGroupWorks } from "../../../../state";
import {
  formatNameWithPassedStatus,
  isFiscalMonthPassed,
} from "../../../../libs/Util";

import styles from "./GroupWork.module.scss";

type Props = {
  onClose: () => void;
  onBack: () => void;
  teachingHintGroupWork: ITeachingHintsGroupWorks | null;
  teacherSurvey: ITeacherSurvey | null;
  onNavigateToSurvey?: () => void;
};

const TAB_HINT_TYPES = [
  "problem_solving",
  "ideal_student_image",
  "event_related",
] as const;

const STUDENT_ISSUES_HEADERS = [
  "生徒に感じる課題",
  "理想の生徒像",
  "今年度のイベント",
];

const GroupWork: React.FC<Props> = ({
  onClose,
  onBack,
  teachingHintGroupWork,
  teacherSurvey,
  onNavigateToSurvey,
}) => {
  const [tab, setTab] = React.useState(0);

  const selectedHintType = TAB_HINT_TYPES[tab];

  const selectedHint = React.useMemo(() => {
    return teachingHintGroupWork?.hints.find(
      hint => hint.hint_type === selectedHintType,
    );
  }, [teachingHintGroupWork, selectedHintType]);

  const renderTabContent = React.useMemo(() => {
    switch (tab) {
      case TAB_HINT_TYPES.indexOf("problem_solving"):
        return <li>{teacherSurvey?.class_challenges}</li>;

      case TAB_HINT_TYPES.indexOf("ideal_student_image"):
        return <li>{teacherSurvey?.desired_student_outcomes}</li>;

      case TAB_HINT_TYPES.indexOf("event_related"):
        return teacherSurvey?.school_events?.map((event, index) => (
          <li key={index}>
            <span
              style={{
                color: isFiscalMonthPassed(event.month) ? "#999" : "inherit",
              }}
            >
              {formatNameWithPassedStatus(event)}
            </span>
            {index < teacherSurvey.school_events.length - 1 && " / "}
          </li>
        ));

      default:
        return null;
    }
  }, [tab, teacherSurvey]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.leftPortion}>
          <button onClick={onBack}>
            <ArrowLeft />
          </button>
          <h1 className={styles.headerText}>
            グループワークの進め方を考えてみたよ！
          </h1>
        </div>
        <button className={styles.closeButton}>
          <CloseIcon onClick={onClose} />
        </button>
      </div>
      <div className={styles.content}>
        {/*固定文で運用することになったが、後ほど各lessonごとに動的に変わる想定とのことでレスポンスからの取得は残しておく*/}
        {/*<p>{teachingHintGroupWork?.group_work_explanation}</p>*/}
        <p>
          生徒のみなさんの状況に合わせた、話し合いを深めるためのグループワークを提案するよ。
          <br />
          意見の共有だけではなく、一歩踏み込んで話し合いをするときのアイデアとして先生から呼びかけてみてね。
        </p>

        <div className={styles.tabs}>
          <div
            className={`${styles.tab} ${
              tab === TAB_HINT_TYPES.indexOf("problem_solving")
                ? styles.activeTab
                : ""
            }`}
            onClick={() => setTab(0)}
          >
            課題の解決へ
            <div className={styles.tab__tail} />
          </div>
          <div
            className={`${styles.tab} ${
              tab === TAB_HINT_TYPES.indexOf("ideal_student_image")
                ? styles.activeTab
                : ""
            }`}
            onClick={() => setTab(1)}
          >
            理想の生徒像へ
            <div className={styles.tab__tail} />
          </div>
          <div
            className={`${styles.tab} ${
              tab === TAB_HINT_TYPES.indexOf("event_related")
                ? styles.activeTab
                : ""
            }`}
            onClick={() => setTab(2)}
          >
            今年度のイベント
            <div className={styles.tab__tail} />
          </div>
        </div>
        <div className={styles.studentIssues}>
          <div className={styles.header}>
            <div className={styles.headerText}>
              {STUDENT_ISSUES_HEADERS[tab]}
            </div>
          </div>
          <div className={styles.body}>
            <ul className={styles.list}>{renderTabContent}</ul>
          </div>
        </div>
        <div className={styles.dynamicContent}>
          <div className={styles.dynamicBody}>
            {selectedHint?.theme &&
            selectedHint?.content &&
            selectedHint?.point ? (
              <>
                <div className={styles.header}>
                  <div className={styles.headerText}>{selectedHint?.theme}</div>
                </div>
                <div className={styles.contentContainer}>
                  <div className={styles.header}>グループワークの深め方</div>
                  <div className={styles.content}>{selectedHint?.content}</div>
                </div>
                <div className={styles.pointContainer}>
                  <div className={styles.pointHeader}>ねらい</div>
                  <div className={styles.pointBody}>{selectedHint?.point}</div>
                </div>
              </>
            ) : (
              <div className={styles.dynamicContent}>
                <div className={styles.warning}>
                  <WarningIcon />
                  まだヒントがありません
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={styles.footer}>
          {selectedHint?.theme && selectedHint?.content && selectedHint?.point
            ? "ぼくのヒントを参考に、生徒のみなさんが対話しやすくなるような声かけをしてみてね！\n" +
              "(AIの特性上、時々間違ったことを言っちゃうかも…。だから、伝える前に内容を確認してもらえると嬉しいな！)"
            : ""}
          {(!selectedHint?.theme ||
            !selectedHint?.content ||
            !selectedHint?.point) && (
            <div className={styles.buttonContainer}>
              <button onClick={onNavigateToSurvey}>
                最新の情報を入力してヒントを生成する <RightArrowIcon />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupWork;
